import { LegalPageLayout } from '@/components/legal/LegalPageLayout'

export default function TermsOfServicePage() {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="December 9, 2025">
      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using EcoSemantic ("Service," "Platform"), operated by Kimel Tech SpA
          ("we," "our," "Company"), you agree to be bound by these Terms of Service ("Terms"). If
          you disagree with any part, you may not access the Service.
        </p>
        <p>
          <strong>Contact:</strong>
          <br />
          Email: support@ecosemantic.com
          <br />
          Company: Kimel Tech SpA, Chile
        </p>
      </section>

      <section>
        <h2>2. Description of Service</h2>
        <p>
          EcoSemantic is a SaaS platform providing AI-native sustainability intelligence and Life
          Cycle Assessment (LCA) tools through Model Context Protocol (MCP) integration with AI
          assistants.
        </p>
      </section>

      <section>
        <h2>3. Account Registration</h2>

        <h3>3.1 Eligibility</h3>
        <ul>
          <li>You must be at least 16 years old</li>
          <li>You must provide accurate information</li>
          <li>One account per user</li>
        </ul>

        <h3>3.2 Account Security</h3>
        <ul>
          <li>You are responsible for maintaining password confidentiality</li>
          <li>You are responsible for all activities under your account</li>
          <li>Notify us immediately of unauthorized access</li>
        </ul>

        <h3>3.3 Account Termination</h3>
        <p>
          <strong>Termination by You:</strong>
        </p>
        <ul>
          <li>
            <strong>Cancellation:</strong> Subscription ends, account remains on Free tier
          </li>
          <li>
            <strong>Deletion:</strong> Account permanently removed, personal data anonymized
            immediately, chat history retained as anonymized data
          </li>
        </ul>

        <p>
          <strong>Termination by Us:</strong>
        </p>
        <p>
          We may suspend or terminate accounts immediately for Terms violations, non-payment,
          fraudulent activity, or at our discretion with 30 days notice.
        </p>
      </section>

      <section>
        <h2>4. Subscription Plans and Payment</h2>

        <h3>4.1 Subscription Tiers</h3>
        <p>
          We offer multiple subscription tiers with current pricing available at
          ecosemantic.com/pricing
        </p>

        <h3>4.2 Payment Processing</h3>
        <ul>
          <li>Payments processed through Paddle.com</li>
          <li>Subscriptions renew automatically unless cancelled</li>
          <li>Prices include applicable VAT/taxes</li>
        </ul>

        <h3>4.3 Billing Cycle</h3>
        <ul>
          <li>Subscriptions billed monthly or annually</li>
          <li>Charges occur on renewal date</li>
          <li>Failed payments may result in service restriction</li>
        </ul>

        <h3>4.4 Failed Payments</h3>
        <p>
          <strong>If a payment fails:</strong>
        </p>
        <ul>
          <li>We will notify you by email immediately</li>
          <li>We will automatically retry the payment over the following days</li>
          <li>During this period, your account may be restricted to Free tier features</li>
          <li>Once payment succeeds, full access is restored automatically</li>
          <li>If all retry attempts fail, your subscription will be cancelled</li>
        </ul>
        <p>
          To avoid service interruption, please ensure your payment method is up to date and has
          sufficient funds available before your renewal date.
        </p>

        <h3>4.5 Price Changes</h3>
        <p>
          We may change prices with 30 days notice. Existing subscriptions maintain current price
          until renewal. Continued use after notice constitutes acceptance.
        </p>

        <h3>4.6 Active Subscription Management</h3>
        <p>
          <strong>If You Have an Active Subscription:</strong>
        </p>
        <ul>
          <li>Deleting your account cancels the subscription</li>
          <li>Access continues until the end of your current billing period</li>
          <li>
            No refund for remaining subscription period (except within 14-day window for initial
            purchase - see Section 5.3)
          </li>
          <li>You cannot re-register with same email while subscription is active</li>
        </ul>
      </section>

      <section>
        <h2>5. Cancellation, Refunds, and Account Deletion</h2>

        <h3>5.1 Subscription Cancellation</h3>
        <p>
          <strong>How to Cancel:</strong>
        </p>
        <ul>
          <li>Through your Account Settings</li>
          <li>Through your Paddle billing portal</li>
          <li>By contacting support@ecosemantic.com</li>
        </ul>
        <p>
          <strong>Cancellation Effect:</strong>
        </p>
        <ul>
          <li>Scheduled for end of current billing period</li>
          <li>Access continues until paid period expires</li>
          <li>Account automatically downgrades to Free tier</li>
          <li>No future charges</li>
          <li>
            No refund for current billing period (except within 14-day window for initial purchase -
            see Section 5.3)
          </li>
        </ul>

        <h3>5.2 Account Deletion</h3>
        <p>
          <strong>How to Delete:</strong>
        </p>
        <ol>
          <li>Log into your account</li>
          <li>Navigate to Account Settings</li>
          <li>Click "Delete Account"</li>
          <li>Confirm deletion</li>
        </ol>

        <p>
          <strong>What Happens:</strong>
        </p>
        <ul>
          <li>Personal information anonymized immediately</li>
          <li>All API tokens revoked</li>
          <li>
            Active subscriptions cancelled (access continues until billing period ends; refund only
            available within 14 days of initial purchase per Section 5.3)
          </li>
          <li>Access to chat history removed (data anonymized and retained for analytics)</li>
          <li>Account login access removed</li>
        </ul>

        <p>
          <strong>What We Retain:</strong>
        </p>
        <ul>
          <li>Financial records (7 years - tax law)</li>
          <li>Transaction history (7 years - accounting)</li>
          <li>Deletion audit log (legal compliance)</li>
          <li>Chat conversation history (anonymized for analytics and service improvement)</li>
        </ul>

        <p>
          <strong>Important:</strong> Account deletion is permanent and irreversible. You cannot
          recover data after deletion.
        </p>

        <h3>5.3 Refund Policy</h3>

        <p>
          <strong>14-Day Refund Period (Initial Purchase Only):</strong>
        </p>
        <p>
          Customers may request a full refund within 14 days of their initial subscription purchase.
        </p>

        <p>
          <strong>Eligibility:</strong>
        </p>
        <ul>
          <li>Applies to first-time subscription purchases only (Enthusiast, Professional)</li>
          <li>Does NOT apply to subscription renewals</li>
          <li>Does NOT apply to one-time call pack purchases</li>
          <li>Request must be made within 14 days of initial purchase date</li>
        </ul>

        <p>
          <strong>How to Request a Refund:</strong>
        </p>
        <ol>
          <li>Email support@ecosemantic.com within 14 days of initial purchase</li>
          <li>Include your transaction ID and order email address</li>
          <li>Specify reason for refund (optional)</li>
          <li>Refunds processed within 5-7 business days via original payment method</li>
        </ol>

        <p>
          <strong>After 14 Days or On Renewals:</strong>
        </p>
        <ul>
          <li>Subscriptions are non-refundable</li>
          <li>Subscription renewals are non-refundable</li>
          <li>Cancellation takes effect at end of current billing period</li>
          <li>No prorated refunds for partial periods</li>
          <li>Access continues until paid period expires</li>
        </ul>

        <p>
          <strong>Exceptions (at our sole discretion):</strong>
        </p>
        <ul>
          <li>Technical issues preventing service access lasting more than 48 hours</li>
          <li>Duplicate charges due to payment processing errors</li>
          <li>Service outages as defined in Section 9</li>
        </ul>

        <p>
          <strong>To Request Exception:</strong>
        </p>
        <p>Email support@ecosemantic.com with transaction details and description of the issue.</p>

        <h3>5.4 Call Packs</h3>
        <ul>
          <li>One-time purchases</li>
          <li>Non-refundable once purchased</li>
          <li>Call amount valid for 12 months from purchase date</li>
        </ul>

        <h3>5.5 Upgrades</h3>
        <ul>
          <li>New subscription take effect immediately</li>
          <li>Prorated for difference between tier prices</li>
          <li>Remaining call amounts are reset for new tier</li>
          <li>New features available immediately</li>
          <li>Billing cycle remains unchanged</li>
        </ul>

        <h3>5.6 Downgrades</h3>
        <ul>
          <li>Effective at end of current billing cycle</li>
          <li>No refund for difference between tier prices</li>
          <li>Current features retained until period end</li>
        </ul>
      </section>

      <section>
        <h2>6. Acceptable Use</h2>
        <p>You agree NOT to:</p>
        <ul>
          <li>Violate any laws or regulations</li>
          <li>Infringe intellectual property rights</li>
          <li>Transmit malware or harmful code</li>
          <li>Attempt unauthorized access to systems</li>
          <li>Use Service for illegal purposes</li>
          <li>Resell or redistribute access</li>
          <li>Abuse or overload our infrastructure</li>
          <li>Reverse engineer the platform</li>
        </ul>

        <h3>6.1 API Usage</h3>
        <ul>
          <li>Subject to tier limits</li>
          <li>Excessive usage may result in throttling</li>
          <li>Intentional abuse may result in termination</li>
        </ul>
      </section>

      <section>
        <h2>7. Intellectual Property</h2>

        <h3>7.1 Our IP</h3>
        <p>
          EcoSemantic platform, code, and branding are our property. You receive a limited license
          to use the Service. No transfer of ownership rights.
        </p>

        <h3>7.2 Your Content</h3>
        <p>
          You retain ownership of data you input. You grant us license to process your data to
          provide the Service. We do not claim ownership of your analysis results.
        </p>

        <h3>7.3 Ecoinvent Data</h3>
        <p>LCA data sourced from Ecoinvent database, used with appropriate permissions.</p>
      </section>

      <section>
        <h2>8. Data and Privacy</h2>
        <p>
          Your use is subject to our Privacy Policy. We process data as described in Privacy Policy.
          See Privacy Policy for GDPR rights and procedures.
        </p>
      </section>

      <section>
        <h2>9. Service Availability</h2>

        <h3>9.1 Uptime</h3>
        <ul>
          <li>We aim for high availability but don't guarantee 100% uptime</li>
          <li>Maintenance windows may cause temporary unavailability</li>
          <li>We do not provide Service Level Agreements (SLAs)</li>
        </ul>

        <h3>9.2 Service Modifications</h3>
        <p>
          We may modify, suspend, or discontinue features with reasonable notice for significant
          changes.
        </p>
      </section>

      <section>
        <h2>10. Limitations of Liability</h2>
        <p>
          <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
        </p>
        <ul>
          <li>Service provided "AS IS" without warranties</li>
          <li>We are not liable for indirect, incidental, or consequential damages</li>
          <li>Total liability limited to fees paid in last 12 months</li>
          <li>We don't warrant accuracy of AI-generated content or LCA results</li>
          <li>Users responsible for validating results for their use cases</li>
        </ul>

        <h3>10.1 Professional Use</h3>
        <ul>
          <li>Results are for informational purposes</li>
          <li>Not a substitute for professional environmental consultation</li>
          <li>Users must validate data for regulatory/compliance purposes</li>
        </ul>
      </section>

      <section>
        <h2>11. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless Kimel Tech SpA from claims arising from your use
          of the Service, violation of these Terms, or violation of any rights of another party.
        </p>
      </section>

      <section>
        <h2>12. Dispute Resolution</h2>

        <h3>12.1 Governing Law</h3>
        <p>These Terms governed by laws of Chile.</p>

        <h3>12.2 Disputes</h3>
        <ul>
          <li>First, contact us to resolve informally</li>
          <li>If unresolved, disputes subject to jurisdiction of Chilean courts</li>
          <li>EU users: Right to use EU dispute resolution mechanisms</li>
        </ul>
      </section>

      <section>
        <h2>13. General Provisions</h2>

        <h3>13.1 Entire Agreement</h3>
        <p>These Terms constitute entire agreement between parties.</p>

        <h3>13.2 Severability</h3>
        <p>If any provision is invalid, remainder remains in effect.</p>

        <h3>13.3 No Waiver</h3>
        <p>Failure to enforce any right doesn't waive that right.</p>

        <h3>13.4 Assignment</h3>
        <p>You may not assign these Terms; we may assign to affiliates or acquirers.</p>
      </section>

      <section>
        <h2>14. Effect of Termination</h2>
        <p>
          <strong>Upon termination:</strong>
        </p>
        <ul>
          <li>
            Service access terminated (or continues until billing period end if subscription active)
          </li>
          <li>API tokens revoked</li>
          <li>Access to chat history removed (data anonymized and retained for analytics)</li>
          <li>Personal information anonymized</li>
          <li>Financial records retained (7 years - legal requirement)</li>
        </ul>

        <p>
          <strong>Your Responsibilities:</strong>
        </p>
        <ul>
          <li>Export your data BEFORE deletion</li>
          <li>Ensure no outstanding payments</li>
          <li>Cancel integrations using your API tokens</li>
        </ul>

        <p>
          <strong>Post-Termination:</strong>
        </p>
        <ul>
          <li>You will no longer be able to access your data through the platform</li>
          <li>Data not exported before deletion cannot be recovered</li>
          <li>You remain liable for charges incurred before termination</li>
        </ul>
      </section>

      <section>
        <h2>15. Changes to Terms</h2>
        <p>
          We may modify Terms with 30 days notice via email or platform notification. Continued use
          constitutes acceptance. Material changes will be clearly communicated.
        </p>
      </section>

      <section>
        <h2>16. Contact Information</h2>
        <p>
          Questions about these Terms:
          <br />
          <strong>Email:</strong> support@ecosemantic.com
          <br />
          <strong>Company:</strong> Kimel Tech SpA, Chile
        </p>
      </section>

      <div className="mt-12 p-6 bg-muted rounded-lg">
        <p className="text-sm">
          <strong>
            By using EcoSemantic, you acknowledge that you have read, understood, and agree to be
            bound by these Terms of Service.
          </strong>
        </p>
      </div>
    </LegalPageLayout>
  )
}
